'use client';

import { useState } from 'react';
import { Copy, Plus, X } from 'lucide-react';
import { tw } from '@tw';
import { ALL_END_TIMES, ALL_TIMES, hhmmToMinutes } from '@/components/availability/timeUtils';
import {
  addBlock,
  blocksByDay,
  copyDay,
  removeBlock,
  setDayEnabled,
  updateBlock,
  validateBlocks,
  type BlockSet,
} from '@/lib/availability/blocks';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * The week the way Cal.com draws it: a switch per day, a start and an end per
 * range, + for another range, and "Copy times to" for the owner whose days
 * match. Every seller's week fits it, and nobody has to learn it. The week
 * belongs to its holder, the availability store.
 */
export function HoursEditor({
  week,
  onWeek,
  idPrefix,
}: {
  week: BlockSet;
  onWeek: (next: BlockSet) => void;
  /** New blocks' id prefix, the holder's own. */
  idPrefix: string;
}) {
  const [copyFrom, setCopyFrom] = useState<number | null>(null);
  const byDay = blocksByDay(week.blocks);
  const errors = validateBlocks(week.blocks.filter((b) => !week.disabledDays.has(b.day)));

  return (
    <div className="flex flex-col divide-y divide-[var(--sf-line)]">
      {DAYS.map((name, day) => {
        const open = !week.disabledDays.has(day);
        const ranges = byDay.get(day) ?? [];
        return (
          <div key={day} className="flex flex-col gap-2 py-2.5">
            <div className="flex min-h-11 items-center gap-3">
              <DaySwitch
                on={open}
                label={`${name} available`}
                onClick={() => {
                  setCopyFrom(null);
                  onWeek(setDayEnabled(week, day, !open, idPrefix));
                }}
              />
              <span
                className={tw(
                  'flex-1 text-p-3 font-semibold',
                  open ? 'text-[var(--sf-ink)]' : 'text-[var(--sf-fnt)]'
                )}
              >
                {name}
              </span>
              {open ? (
                <>
                  <IconButton
                    label={`Add hours on ${name}`}
                    onClick={() => onWeek(addBlock(week, day, idPrefix))}
                  >
                    <Plus className="size-4" />
                  </IconButton>
                  <IconButton
                    label={`Copy ${name}'s hours to other days`}
                    pressed={copyFrom === day}
                    onClick={() => setCopyFrom(copyFrom === day ? null : day)}
                  >
                    <Copy className="size-4" />
                  </IconButton>
                </>
              ) : (
                <span className="text-p-3 text-[var(--sf-fnt)]">Unavailable</span>
              )}
            </div>

            {open &&
              ranges.map((block) => (
                <div key={block.id} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <TimeSelect
                      value={block.start}
                      options={ALL_TIMES}
                      label={`${name} from`}
                      onChange={(start) => onWeek(updateBlock(week, block.id, { start }))}
                    />
                    <span className="text-[var(--sf-fnt)]">to</span>
                    <TimeSelect
                      value={block.end}
                      options={ALL_END_TIMES.filter(
                        (t) => hhmmToMinutes(t) > hhmmToMinutes(block.start)
                      )}
                      label={`${name} to`}
                      onChange={(end) => onWeek(updateBlock(week, block.id, { end }))}
                    />
                    {ranges.length > 1 && (
                      <IconButton
                        label={`Remove ${block.start} to ${block.end}`}
                        onClick={() => onWeek(removeBlock(week, block.id))}
                      >
                        <X className="size-4" />
                      </IconButton>
                    )}
                  </div>
                  {errors.get(block.id) && (
                    <span className="sf-note text-error">{errors.get(block.id)}</span>
                  )}
                </div>
              ))}

            {copyFrom === day && (
              <CopyTo
                from={day}
                onCancel={() => setCopyFrom(null)}
                onApply={(to) => {
                  setCopyFrom(null);
                  onWeek(copyDay(week, day, to, idPrefix));
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Cal.com's "Copy times to", opened in place under the day it copies. */
function CopyTo({
  from,
  onApply,
  onCancel,
}: {
  from: number;
  onApply: (to: number[]) => void;
  onCancel: () => void;
}) {
  const others = DAYS.map((_, d) => d).filter((d) => d !== from);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const all = picked.size === others.length;
  const toggle = (d: number) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });

  return (
    <div className="flex flex-col gap-2 rounded-2xl bg-[var(--sf-page)] p-3.5">
      <span className="text-p-3 font-bold text-[var(--sf-ink)]">Copy times to</span>
      <Check checked={all} onChange={() => setPicked(all ? new Set() : new Set(others))}>
        Select all
      </Check>
      <div className="grid grid-cols-2 gap-x-3">
        {DAYS.map((name, d) => (
          <Check
            key={d}
            checked={d === from || picked.has(d)}
            disabled={d === from}
            onChange={() => toggle(d)}
          >
            {name}
          </Check>
        ))}
      </div>
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="mm-btn min-h-11 px-2 text-p-3 font-semibold text-[var(--sf-mut)]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={picked.size === 0}
          onClick={() => onApply([...picked])}
          className="mm-btn min-h-11 rounded-full bg-[var(--sf-ink)] px-5 text-p-3 font-bold text-[var(--sf-page)] disabled:opacity-35"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

function Check({
  checked,
  disabled = false,
  onChange,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  children: string;
}) {
  return (
    <label
      className={tw(
        'flex min-h-10 items-center gap-2.5 text-p-3 font-semibold',
        disabled ? 'text-[var(--sf-fnt)]' : 'cursor-pointer text-[var(--sf-ink)]'
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="size-4 accent-[var(--sf-ink)]"
      />
      {children}
    </label>
  );
}

/** The product cards' deal switch, at the size of a row. */
function DaySwitch({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={tw(
        'relative h-7 w-12 shrink-0 rounded-full transition-colors',
        on ? 'bg-[var(--sf-ink)]' : 'bg-[var(--sf-line)]'
      )}
    >
      <span
        className={tw(
          'absolute left-1 top-1 size-5 rounded-full bg-[var(--sf-card)] transition-transform',
          on && 'translate-x-5'
        )}
      />
    </button>
  );
}

function IconButton({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={tw(
        'mm-btn flex size-10 shrink-0 items-center justify-center rounded-full transition-colors',
        pressed
          ? 'bg-[var(--sf-ink)] text-[var(--sf-page)]'
          : 'text-[var(--sf-mut)] hover:bg-[var(--sf-page)] hover:text-[var(--sf-ink)]'
      )}
    >
      {children}
    </button>
  );
}

function TimeSelect({
  value,
  options,
  label,
  onChange,
}: {
  value: string;
  options: string[];
  label: string;
  onChange: (next: string) => void;
}) {
  // A start moved past the end pushes the end on; show the first end still offered.
  const safe = options.includes(value) ? value : (options[0] ?? value);
  return (
    <select
      value={safe}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
      // 16px on a phone or iOS Safari zooms the card on focus.
      className="sf-num min-h-11 min-w-0 flex-1 rounded-xl border border-[var(--sf-line)] bg-[var(--sf-card)] px-3 text-[16px] font-semibold text-[var(--sf-ink)] outline-none sm:text-p-3"
    >
      {options.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}
